import React, { ComponentProps, useCallback, useEffect, useMemo, useState } from "react"

import get from "lodash.get"

import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from "@mui/material"

import { camelCaseToSpaceSeparated } from "utils/stringUtils"

import { cloneFormData } from "./formUtils"

const DIALOG_TRANSITION_PROPS: ComponentProps<typeof Dialog>["TransitionProps"] = { unmountOnExit: true }

export interface ParentSiblingArrayCopyDefinition {
  fieldDepth: string[]
  parentTitleProperty?: string
  uncopyableProperties?: string[]
}

interface Props<T> {
  formData: T
  copyDefinition?: ParentSiblingArrayCopyDefinition
  onCopyAndAdd: (fieldDepth: string[], value: unknown[]) => void
  onDismiss: () => void
}

export default function ParentSiblingArrayCopyModal<T>(props: Props<T>) {
  const { formData, copyDefinition, onCopyAndAdd, onDismiss } = props
  const [siblingCopyTarget, setSiblingCopyTarget] = useState<string>("")

  const { arrayName, copyOptions, parentTitle, arrayTitle, parentInstance } = useMemo(() => {
    if (!copyDefinition) return {}

    const { fieldDepth, parentTitleProperty } = copyDefinition
    const [parentName, parentIdx, arrayName] = fieldDepth.slice(
      fieldDepth.length - 3 // the last 3 entries, if existing, represent the relevant identifiers
    )

    const parentIndex = Number(parentIdx)

    const parentTitle: string = camelCaseToSpaceSeparated(parentName).toLowerCase()
    const arrayTitle: string = camelCaseToSpaceSeparated(arrayName).toLowerCase()

    const parentInstance: unknown[] = get(formData, fieldDepth.slice(0, fieldDepth.length - 2))

    const copyOptions =
      parentInstance?.map((sibling, index) => {
        let label = `${parentTitle} ${index + 1}`
        const title: string | undefined = sibling[parentTitleProperty]

        if (title) {
          label += ` [${title}]`
        }
        label += ` ${arrayTitle}`

        const isCurrent = index === parentIndex

        if (isCurrent) {
          label += " [CURRENT]"
        }

        return {
          disabled: isCurrent,
          label,
          value: String(index)
        }
      }) ?? []

    return {
      arrayName,
      copyOptions,
      arrayTitle,
      parentTitle,
      parentInstance
    }
  }, [copyDefinition, formData])

  const handleRadioChange = useCallback<ComponentProps<typeof RadioGroup>["onChange"]>((e) => {
    setSiblingCopyTarget(e.target.value)
  }, [])

  const handleCopyAndAdd = useCallback(() => {
    const items: unknown[] = cloneFormData(
      parentInstance[siblingCopyTarget][arrayName],
      copyDefinition.uncopyableProperties
    )

    onCopyAndAdd(copyDefinition.fieldDepth, items)
  }, [arrayName, copyDefinition, onCopyAndAdd, parentInstance, siblingCopyTarget])

  useEffect(() => {
    setSiblingCopyTarget("")
  }, [copyDefinition])

  if (!copyDefinition) {
    return null
  }

  return (
    <Dialog open={!!copyDefinition} TransitionProps={DIALOG_TRANSITION_PROPS} onClose={onDismiss}>
      <DialogTitle>
        Copying{" "}
        <Typography color="primary" variant="inherit" component="span">
          {arrayTitle}
        </Typography>{" "}
        from sibling{" "}
        <Typography color="primary" variant="inherit" component="span">
          {parentTitle}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <RadioGroup value={siblingCopyTarget} onChange={handleRadioChange}>
          {copyOptions.map((entity) => (
            <FormControlLabel
              key={`entity-${entity.value}`}
              value={entity.value}
              disabled={entity.disabled}
              control={<Radio />}
              label={entity.label}
            />
          ))}
          {!copyOptions.length && <Alert severity="warning">No parent sibling available</Alert>}
        </RadioGroup>
        {!!copyDefinition?.uncopyableProperties?.length && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" gap={1} alignItems="center">
              <Typography variant="caption">These properties will be omitted:</Typography>
              {copyDefinition.uncopyableProperties.map((property, index) => (
                <Chip key={`${property}-${index}`} size="small" variant="outlined" label={property} />
              ))}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onDismiss}>Dismiss</Button>
        <Button variant="contained" disabled={!siblingCopyTarget} onClick={handleCopyAndAdd}>
          Copy and add
        </Button>
      </DialogActions>
    </Dialog>
  )
}
